import { CellContext, createColumnHelper } from '@tanstack/react-table';
import { FC, useEffect, useMemo } from 'react';
import { Flex } from 'antd';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import { useWatch } from 'react-hook-form';
import styled from 'styled-components';
import {
  CreateFormTableRowButton,
  DeleteFormTableRowButton,
  FormField,
  FormInput,
  getFormTableFieldName,
  TanstackFormTable,
  RequiredText,
  User,
  formatDateTime,
  useUserInfo,
  DataSubjectFormValidDataType,
} from '@atlas-frontend/sdk';
import translations from './DataSubjectCommentsTable.translations.json';

const { addTranslations, useTranslation } = i18nNsFactory(
  'data-subject-comments-table',
);
addTranslations(translations);
const columnHelper = createColumnHelper<DataSubjectFormValidDataType>();

function isEditable(
  props: CellContext<DataSubjectFormValidDataType, any>,
  user: User,
) {
  return (
    !props.row.original.id || props.row.original.createdBy?.id === user?.id
  );
}

export interface DataSubjectCommentsEditTableProps {
  onChange?: (comments: DataSubjectFormValidDataType[]) => void;
}

export const DataSubjectCommentsEditTable: FC<
  DataSubjectCommentsEditTableProps
> = (props) => {
  const { onChange } = props;
  const { t } = useTranslation();
  const { user } = useUserInfo();
  const name = 'comments' as const;

  const comments = useWatch({ name });
  useEffect(() => {
    onChange?.(comments);
  }, [onChange, comments]);

  const cols = useMemo(
    () => [
      columnHelper.accessor('createdBy.username', {
        header: t('column.user'),
        meta: {
          width: '20ch',
        },
      }),
      columnHelper.accessor('comments', {
        header: () => <RequiredText>{t('column.comment')}</RequiredText>,
        cell: (props) => {
          const name = getFormTableFieldName(props);

          return (
            <FormField
              name={name}
              size="small"
              preview={{
                previewOnly: !isEditable(props, user),
              }}
            >
              <FormInput
                autoFocus
                maxLength={2000}
                name={name}
                rows={3}
                size="small"
              />
            </FormField>
          );
        },
        meta: {
          minWidth: 150,
        },
        sortingFn: 'localeCompareCaseSensitive',
      }),
      columnHelper.accessor((rowData) => formatDateTime(rowData.createdAt), {
        header: t('column.createdAt'),
        cell: (props) => props.renderValue(),
        filterFn: 'formattedDate',
        meta: {
          width: 200,
        },
      }),
      columnHelper.accessor((rowData) => formatDateTime(rowData.modifiedAt), {
        id: 'modifiedAt',
        header: t('column.modifiedAt'),
        cell: (props) => props.renderValue(),
        filterFn: 'formattedDate',
        sortingFn: 'formattedDateTime',
        meta: {
          width: 200,
        },
      }),
      columnHelper.accessor('operations' as any, {
        header: () => (
          <Flex justify="center" gap="small">
            <CreateFormTableRowButton autoFocusField="comments" />
          </Flex>
        ),
        enableSorting: false,
        enableColumnFilter: false,
        cell: (props) =>
          isEditable(props, user) && (
            <Flex justify="center" gap="small">
              <DeleteFormTableRowButton id="delete-comment" context={props} />
            </Flex>
          ),
        meta: {
          width: 30,
          className: 'sticky-right',
        },
      }),
    ],
    [t, user],
  );

  return (
    <TableWrapper>
      <TanstackFormTable
        name={name}
        columns={cols}
        defaultSort={[{ id: 'modifiedAt', desc: true }]}
        sticky
        virtual
        scrollContainerSelector="#detail-layout-main-content"
      />
    </TableWrapper>
  );
};

const TableWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;
